import React, { useCallback, useRef, useState } from 'react';
import * as yup from 'yup';
import { SxStyle } from '../../../types';
import Box from '@mui/material/Box';
import { useSnackbar } from 'notistack';
import { useStringsContext } from '../../../contexts/strings';
import { List, ListItem, ListItemText, Stack } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import LoadingButton from '@mui/lab/LoadingButton';
import P, { InferProps } from 'prop-types';
import TextInput from './text-field';
import { isAllFieldsValid } from '../../../utils/team';
import { FIELD_TYPES } from '../../../types';
import { useRSSValidate, ValidateData } from '../../../hooks/rss';

const styles: SxStyle = {
  fieldContainer: (theme) => ({
    width: '350px',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  }),
  button: {
    width: '250px',
  },
};

function ValidateRSSField({
  setChange,
  setValidation,
  field,
  isReadOnly,
}: InferProps<typeof ValidateRSSField.propTypes>) {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();
  const validateUrl = useRSSValidate();
  const [isValidating, setValidating] = useState(false);
  const [urlLink, setLinkUrl] = useState<string | null>(field.value);
  const [validatedData, setValiddatedData] = useState<ValidateData | null>(null);
  const editedFields = useRef<Record<string, boolean>>({});
  const textField = useRef<any>({
    title: strings.fields.newsRSSLink,
    id: 'news-rss-link',
    type: FIELD_TYPES.TEXT,
    key: 'newsRssUrl',
    value: field.value,
    validation: yup.string().url(strings.validations.validUrl),
  });

  const handleValidation = useCallback(
    async (
      fieldKey: 'newsRssUrl' | 'newsRssData',
      value: string | number | boolean,
      fieldValidation: any
    ) => {
      const isCurrentFieldValid: boolean = await fieldValidation.isValid(value);

      editedFields.current[fieldKey] = isCurrentFieldValid;
      const isFieldValid = isAllFieldsValid(editedFields.current);

      setValidation(field.key, isFieldValid, field.validation);
    },
    [field.key, field.validation, setValidation]
  );

  const onTextChange = useCallback(
    (fieldKey: string, url: string) => {
      setLinkUrl(url);

      setValiddatedData(null);
      if (url && url.length > 0) {
        editedFields.current['newsRssData'] = false;
      } else {
        // Skip validation and allow publish if url is empty
        editedFields.current['newsRssData'] = true;
      }

      setChange(field.key, url, field.validation, field.isTeamDataValue);
    },
    [field, setChange]
  );

  const onValidateButton = useCallback(async () => {
    if (!urlLink) {
      return;
    }

    setValidating(true);

    try {
      const validatedData = await validateUrl(urlLink);

      if (validatedData) {
        const validationValue = Object.values(validatedData).every((value) => value);

        setValiddatedData(validatedData);

        // Do not validate and allow publish if news are missing (i.e. link broken etc)
        handleValidation('newsRssData', validatedData.validFeed, yup.boolean().isTrue());

        if (validationValue) {
          enqueueSnackbar(strings.messages.validateSuccess, {
            variant: 'success',
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setValidating(false);
    }
  }, [enqueueSnackbar, handleValidation, strings, urlLink, validateUrl]);

  return (
    <Stack spacing={1}>
      <Box sx={styles.fieldContainer}>
        <TextInput
          setChange={onTextChange}
          setValidation={handleValidation}
          field={textField.current}
          isReadOnly={isReadOnly}
          fullWidth
        />
      </Box>
      <List dense={true}>
        {validatedData && (
          <>
            {!validatedData.validFeed && (
              <ListItem disablePadding>
                <Stack spacing={2} direction="row" alignItems="center">
                  <ErrorIcon color="error" />
                  <ListItemText primary={strings.validations.validNewsRSS} />
                </Stack>
              </ListItem>
            )}
            {validatedData.validFeed && !validatedData.validImage && (
              <ListItem disablePadding>
                <Stack spacing={2} direction="row" alignItems="center">
                  <ErrorIcon color="error" />
                  <ListItemText primary={strings.validations.validNewsRSSImage} />
                </Stack>
              </ListItem>
            )}
            {validatedData.validFeed && !validatedData.validTitle && (
              <ListItem disablePadding>
                <Stack spacing={2} direction="row" alignItems="center">
                  <ErrorIcon color="error" />
                  <ListItemText primary={strings.validations.validNewsRSSTitle} />
                </Stack>
              </ListItem>
            )}
            {validatedData.validFeed && !validatedData.validLink && (
              <ListItem disablePadding>
                <Stack spacing={2} direction="row" alignItems="center">
                  <ErrorIcon color="error" />
                  <ListItemText primary={strings.validations.validNewsRSSLink} />
                </Stack>
              </ListItem>
            )}
          </>
        )}
      </List>
      <LoadingButton
        sx={styles.button}
        onClick={onValidateButton}
        type="submit"
        variant="contained"
        color="primary"
        startIcon={<PublishedWithChangesIcon />}
        loading={isValidating}
        disabled={urlLink?.length === 0 || !editedFields.current.newsRssUrl}
      >
        {strings.actions.validateRss}
      </LoadingButton>
    </Stack>
  );
}

ValidateRSSField.propTypes = {
  field: P.shape({
    title: P.string.isRequired,
    id: P.string.isRequired,
    type: P.string.isRequired,
    validation: P.any,
    key: P.string,
    value: P.string.isRequired,
    isTeamDataValue: P.bool,
  }).isRequired,
  setChange: P.func.isRequired,
  setValidation: P.func.isRequired,
  isReadOnly: P.bool,
};

export default React.memo(ValidateRSSField);
